import React, {Component} from 'react';
import {func} from 'prop-types';
import invoke from 'lodash.invoke';
import __ from 'ramda/src/__.js';
// import or from 'ramda/src/or.js';
import and from 'ramda/src/and.js';
import map from 'ramda/src/map.js';
import prop from 'ramda/src/prop.js';
import gte from 'ramda/src/gte.js';
import compose from 'ramda/src/compose.js';
import length from 'ramda/src/length.js';
import flatten from 'ramda/src/flatten.js';
import pick from 'ramda/src/pick.js';
import equals from 'ramda/src/equals.js';
import {createSearchContext} from '@ultradent/upi-fuzzy-search';
import SearchInput from '../SearchInput';
import SearchResults from '../SearchResults';
import SearchResultsPane from '../SearchResultsPane';
import SearchResultItem from '../SearchResultItem';
import mediaQuery from '@ultradent/utilities/MediaQueries';
import {noop} from '@ultradent/utilities/Functions';

export const SearchContext = React.createContext();

function withSearch ( Comp ) {
    class Wrapper extends Comp {
        render () {
            return (
                <SearchContext.Consumer>
                    {contextValue =>
                        <Comp search={contextValue} {...this.props} />
                    }
                </SearchContext.Consumer>
            );
        }
    }

    Wrapper.displayName = `withSearch(${Comp.displayName || Comp.name})`;
    return Wrapper;
}

class Search extends Component {

    static ResultsPane = SearchResultsPane;
    static ResultItem = SearchResultItem;
    static Input = withSearch( SearchInput );
    static Results = withSearch( SearchResults );

    queryProvider = noop;
    queryErrorCount = 0;
    queryErrorThreshold = 15;

    // start with empty list which gets updated by onResultChange events from SearchResultPagination groups
    visibleResultGroups = [];

    static defaultProps = {
        initialQuery: '',
        alwaysShowResults: false,
        hideOnFocusout: false,
        hideOnResultSelect: false,
        showDefaultResults: false,
        showNoResults: true,
        onResultSelect: noop,
        queryGroup: {
            itemSize: 40,
            virtualized: false,
            queryProps: [],
            list: []
        },
        resultLimit: 7,
        minQueryLength: 3
    };

    constructor ( props ) {
        super( props );

        this.state = {
            loading: false,
            error: false,
            selectedIndex: -1,
            isFocused: false,
            isActive: false,
            hideOnFocusout: props.hideOnFocusout,
            initialQueryGroup: props.queryGroup,
            queryGroups: Array.isArray( props.queryGroup ) ? props.queryGroup : [props.queryGroup],
            // if results are from a static Provider, initialize queryGroups with a constructed queryList
            queryProvider: props.queryProvider || Search.createQueryProvider( props.queryGroup ),
            resultGroups: [],
            query: props.initialQuery || '',
            transientQuery: null // temporary query value for populated by results focused from keyboard
        }
    }

    static getDerivedStateFromProps ( props, state ) {

        if ( !equals( props.initialQuery, state.prevInitialQuery ) ) {
            return {
                prevInitialQuery: props.initialQuery,
                query: props.initialQuery
            };
        }

        if ( !equals( props.queryGroup, state.initialQueryGroup ) ) {
            return {
                isDirty: true,
                initialQueryGroup: props.queryGroup,
                queryGroups: Array.isArray( props.queryGroup ) ? props.queryGroup : [props.queryGroup],
                queryProvider: props.queryProvider || Search.createQueryProvider( props.queryGroup )
            };
        }

        return null;
    }

    static createQueryProvider ( queryGroup ) {
        const queryGroups = Array.isArray( queryGroup ) ? queryGroup : [queryGroup];

        // create a list of searchContexts each with their own data sets that can later be queried
        return queryGroups.map( grp => {
            const {list, queryProps, useStrictMatching} = grp;
            const ctx = createSearchContext( list, queryProps, useStrictMatching );

            // note - sorting needs to happen before filtering
            if ( grp.sort ) {
                ctx.setSorting( grp.sort );
            }

            if ( grp.filter ) {
                ctx.setFilters( grp.filter );
            }

            return {...grp, query: ctx.query};
        } );
    }

    shouldComponentUpdate ( nextProps, nextState ) {
        return !equals( this.props, nextProps ) || !equals( this.state, nextState );
    }

    componentDidUpdate ( prevProps, prevState, snapshot ) {
        if ( this.state.isDirty && this.state.isActive ) {
            this.setState( {isDirty: false} );
            this.onSearch( this.state.query );
        }
    }

    componentDidMount () {
        this._ismounted = true;

        // note - immediately initiate search so show default results
        if ( this.props.showDefaultResults ) {
            this.onSearch( this.state.query );
        }

        if ( this.state.hideOnFocusout ) {
            document.addEventListener( 'click', this.hideOnFocusout );
        }
    }

    componentWillUnmount () {
        this._ismounted = false;
        document.removeEventListener( 'click', this.hideOnFocusout );
    }

    hideOnFocusout = ( {target} ) => {
        const breakpointName = mediaQuery.getBreakPointAlias();
        if ( breakpointName === 'small' ) {
            return;
        }
        // click occurred inside the search component or a page modal -> remain active
        while (
            target
            && target.nodeName
            && target !== this.wrapper
            && target.nodeName.toLowerCase() !== 'html' ) {
            target = target.parentNode;
        }
        if ( !target || (target && target.nodeName.toLowerCase() === 'html') ) {
            this.clearSoft();
        }
    }

    // [[{results: a}],[{results: b}]] -> Array [a,b]
    flattenResultGroups = compose( flatten, map( prop( 'results' ) ) );

    // [[{results: a}],[{results: b}]] -> Int
    getResultCount = compose( length, this.flattenResultGroups );

    // [[a],[b]] -> Boolean
    hasResults = compose( gte( __, 1 ), this.getResultCount );

    getActiveState = ( query, doShow ) => and( gte( query.length, this.props.minQueryLength ), doShow );

    getQueriedResults = query => {
        const lastQuery = this.state.query;
        // static queryProvider will be an Array of queryGroups that can be mapped over
        return this.state.queryProvider.map( ( ctx, index ) => {
            const currentResultGroup = this.state.resultGroups[index];
            const {results} = ctx.query( query );
            // apply index & metadata to each result
            return {
                results,
                pageIndex: query === lastQuery && currentResultGroup ? currentResultGroup?.pageIndex : 0, // reset pageIndex only if query changes
                resultLimit: ctx.resultLimit || this.props.resultLimit
            };
        } )
    };

    queryStaticProvider = ( query ) => {
        const {alwaysShowResults, showDefaultResults} = this.props;
        const isActive = showDefaultResults || this.getActiveState( query, alwaysShowResults || this.state.isFocused );

        this.setState( {
            query,
            isActive,
            resultGroups: isActive ? this.getQueriedResults( query ) : []
        } );
    };

    queryApiProvider = async ( query ) => {
        const {alwaysShowResults, showDefaultResults} = this.props;
        const isActive = showDefaultResults || this.getActiveState( query, alwaysShowResults || this.state.isFocused );

        this.setState( state => ({
            query,
            isActive,
            loading: true,
            error: false,
            resultGroups: isActive ? state.resultGroups : []
        }) );

        if ( isActive ) {
            try {
                const resultGroups = await this.state.queryProvider( query );
                this._ismounted && this.setState( {
                    loading: false,
                    resultGroups
                } );
            }
            catch ( error ) {
                this.queryErrorCount += 1;
                this._ismounted && this.setState( {loading: false, error: true} );
            }
        }
    };

    setSelectionIndex = ( index ) => {
        if ( this.props.ignoreSelectedIndex ) {
            // don't track selected index to reduce excessive re-renders
            return;
        }

        // force index boundaries from 0 - maxLength
        const visibleCount = this.getResultCount( this.visibleResultGroups );
        const selectedIndex = Math.min( Math.max( index, -1 ), visibleCount - 1 );
        this.setState( {selectedIndex} );
    };

    getSelectedResultsByIndex = ( index ) => {
        const flatResults = this.flattenResultGroups( this.visibleResultGroups );
        return flatResults[index];
    };

    // CLear EVERYTHING
    clearHard = () => {
        this.visibleResultGroups = [];
        this.setState( {
            query: '',
            transientQuery: null,
            resultGroups: [],
            isActive: false
        } );
    };
    // Preserve QUERY state while clearing everything else
    clearSoft = () => {
        if ( !this.props.alwaysShowResults ) {
            this.applyTransientQuery();
            this.visibleResultGroups = [];
            this.setState( {
                resultGroups: [],
                isActive: false
            } );
        }
    };

    applyTransientQuery = () => {
        if ( this.state.transientQuery ) {
            this.setState( ( {transientQuery} ) => ({
                query: transientQuery,
                transientQuery: null
            }) );
        }
    };

    onVisibleResultsChange = ( index, newResults ) => {
        this.visibleResultGroups[index] = newResults;
    };

    handleResultClick = ( evt, {index} ) => {
        const selection = this.getSelectedResultsByIndex( index );
        this.handleResultSelect( evt, selection );
    };

    handleResultHover = ( evt, {index} ) => {
        this.setSelectionIndex( index );
    };

    handleResultFocus = ( result ) => {
        // apply transientQuery value when results is focused from keyboard
        const transientQueryProp = result?.queryGroup?.transientQueryProp;
        this.setState( {transientQuery: transientQueryProp ? result[transientQueryProp] : null} );

        invoke( this.props, 'onResultFocus', result );
    };

    handleResultSelect = ( evt, result ) => {
        if ( this.props.hideOnResultSelect ) {
            this.clearSoft();
        }

        invoke( this.props, 'onResultSelect', evt, result );
    };

    onSubmit = ( evt ) => {
        this.clearSoft();

        const currentQuery = this.state.transientQuery || this.state.query;
        invoke( this.props, 'onSubmit', evt, currentQuery );
    }

    onEnter = ( evt ) => {
        evt.preventDefault();
        const index = this.state.selectedIndex;
        if ( index === -1 ) {
            this.onSubmit( evt );
            return;
        }

        const selection = this.getSelectedResultsByIndex( index );
        this.handleResultSelect( evt, selection );
    };

    onInput = value => {
        // if receive input while transientQuery; apply transientQuery
        this.applyTransientQuery();
        this.onSearch( value );
    };

    onClear = () => {
        this.clearHard();
    };

    onFocus = () => {
        this.setState( {isFocused: true} );
        this.onSearch( this.state.query );
    };

    onKeyUp = keycode => {
        this.setState( {isFocused: keycode !== 27} );
    };

    onKeyDown = keycode => {
        let index;
        let selection;

        switch ( keycode ) {
            // UP_ARROW
            // decrement selection index
            case 38:
                index = this.state.selectedIndex - 1;
                selection = this.getSelectedResultsByIndex( index );
                this.setSelectionIndex( index );
                this.handleResultFocus( selection );
                break;

            // DOWN_ARROW
            // increment selection index
            case 40:
                index = this.state.selectedIndex + 1;
                selection = this.getSelectedResultsByIndex( index );
                this.setSelectionIndex( index );
                this.handleResultFocus( selection );
                break;

            // ESC
            // make inActive
            case 27:
                this.clearSoft();
                break;

            default:
            // fall through
        }
    };

    onPageIndexChange = ( index, queryGroupIndex = 0 ) => {
        this.setState( state => {
            const {resultGroups} = state;
            const newResultGroups = [...resultGroups];
            newResultGroups[queryGroupIndex] = {...newResultGroups[queryGroupIndex], pageIndex: index};
            return {resultGroups: newResultGroups};
        } );
    };

    onSearch = query => {
        // check if error limit has been exceeded
        if ( this.queryErrorCount >= this.queryErrorThreshold ) {
            console.warn( '[Search:queryApiProvider] - Operation ignored; too many exceptions' );
            this.setState( {
                query,
                isActive: false,
                loading: false,
                error: false,
                resultGroups: []
            } );
            return;
        }

        // reset selectedIndex anytime a query changes
        this.setSelectionIndex( -1 );

        if ( this.props.queryProvider != null ) {
            this.queryApiProvider( query );
            return;
        }

        this.queryStaticProvider( query );
    };

    render () {
        const {
            alwaysShowResults,
            showDefaultResults,
            showNoResults,
            showPagination,
            itemProps,
            children
        } = this.props;
        const {loading, error, isActive, resultGroups, queryGroups} = this.state;

        const eventHandlers = pick( [
            'onClear',
            'onInput',
            'onEnter',
            'onSubmit',
            'onKeyDown',
            'onKeyUp',
            'onFocus',
            'onPageIndexChange',
            'onVisibleResultsChange',
            'handleResultHover',
            'handleResultClick'], this );

        const sendProps = {
            loading,
            error,
            resultGroups,
            queryGroups,
            hasResults: this.hasResults( resultGroups ),
            itemProps,
            isActive,
            alwaysShowResults,
            showDefaultResults,
            showPagination,
            showNoResults,
            ...eventHandlers,
            ...this.state
        };

        return (
            <SearchContext.Provider value={sendProps}>
                <div
                    ref={ref => this.wrapper = ref}
                    data-testid="search-container"
                    className="w-full">
                    {children( sendProps )}
                </div>
            </SearchContext.Provider>
        );
    }
}

Search.propTypes = {
    children: func.isRequired
}

export default Search;



